import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Loader2 } from 'lucide-react';
import BackgroundBlobs from '../components/BackgroundBlobs';
import PuzzleOverlay from '../components/PuzzleOverlay';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const NO_UNLOCK_MESSAGE = "Please start from the Solve Puzzle button to unlock today's free puzzle.";

const FreePuzzlePage = () => {
  const { isAuthenticated, loading, user, solveFreePuzzle, hasSolvedToday, markFreePuzzleWinnerModalSkip } = useAuth();
  const navigate = useNavigate();
  const [statusLoading, setStatusLoading] = useState(true);
  const [canSubmit, setCanSubmit] = useState(false);
  const [message, setMessage] = useState(NO_UNLOCK_MESSAGE);
  const [entryNumber, setEntryNumber] = useState(null);

  useEffect(() => {
    const existingRobots = document.querySelector('meta[name="robots"]');
    const robotsMeta = existingRobots || document.createElement('meta');
    const previousContent = robotsMeta.getAttribute('content');

    robotsMeta.setAttribute('name', 'robots');
    robotsMeta.setAttribute('content', 'noindex,nofollow');
    if (!existingRobots) {
      document.head.appendChild(robotsMeta);
    }

    return () => {
      if (existingRobots) {
        robotsMeta.setAttribute('content', previousContent || '');
      } else {
        robotsMeta.remove();
      }
    };
  }, []);

  useEffect(() => {
    if (loading || !isAuthenticated || !user?.id) return;

    let isMounted = true;
    const loadStatus = async () => {
      try {
        setStatusLoading(true);
        console.info('HARDAY_SPONSOR_UNLOCK_CALLBACK_RECEIVED');
        const { data } = await api.getFreePuzzleSessionStatus(user.id);
        if (!isMounted) return;
        setCanSubmit(Boolean(data?.canSubmit));
        setEntryNumber(data?.entryNumber || data?.nextPuzzleEntryNumber || null);
        setMessage(data?.message || (data?.canSubmit ? '' : NO_UNLOCK_MESSAGE));
        if (data?.canSubmit) {
          console.info('HARDAY_FREE_PUZZLE_UNLOCK_VALID');
          console.info('HARDAY_PUZZLE_PAGE_OPENED');
        }
        if (!data?.canSubmit) {
          console.info('HARDAY_FREE_PUZZLE_UNLOCK_INVALID');
        }
      } catch (error) {
        if (!isMounted) return;
        setCanSubmit(false);
        setMessage(error?.response?.data?.detail || NO_UNLOCK_MESSAGE);
        console.info('HARDAY_FREE_PUZZLE_UNLOCK_INVALID');
      } finally {
        if (isMounted) setStatusLoading(false);
      }
    };

    loadStatus();
    return () => {
      isMounted = false;
    };
  }, [loading, isAuthenticated, user?.id]);

  const goToDashboardAfterFreePuzzle = () => {
    markFreePuzzleWinnerModalSkip();
    navigate('/dashboard', { replace: true, state: { fromFreePuzzle: true, skipWinnerModal: true } });
  };

  if (!loading && !isAuthenticated) {
    return <Navigate to="/login?next=/free-puzzle" replace />;
  }

  if (loading || statusLoading) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen relative overflow-hidden bg-white shadow-2xl flex items-center justify-center">
        <BackgroundBlobs />
        <div className="relative z-10 glass-panel rounded-[22px] p-6 flex items-center gap-3 text-[#666666] font-['DM_Sans']">
          <Loader2 className="w-5 h-5 animate-spin text-[#9333EA]" />
          Checking free puzzle unlock...
        </div>
      </div>
    );
  }

  if (!canSubmit || (hasSolvedToday && Number(user?.dailyPuzzleEntriesCompleted || 0) >= 3)) {
    return (
      <div className="w-full max-w-md mx-auto min-h-screen relative overflow-hidden bg-white shadow-2xl flex items-center justify-center p-5">
        <BackgroundBlobs />
        <motion.div
          className="relative z-10 glass-panel rounded-[24px] p-6 text-center space-y-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold text-[#000000] font-['Outfit']">Free Puzzle Unlock</h1>
          <p className="text-sm text-[#555555] font-['DM_Sans'] leading-relaxed">{hasSolvedToday ? "You have already solved today's puzzle." : message}</p>
          <button
            type="button"
            onClick={goToDashboardAfterFreePuzzle}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Dashboard
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen relative overflow-hidden bg-white shadow-2xl">
      <BackgroundBlobs />
      <PuzzleOverlay
        isOpen
        allowSolvedDisplay
        onClose={goToDashboardAfterFreePuzzle}
        onSubmitAnswer={(selectedOption) => solveFreePuzzle(selectedOption, entryNumber)}
      />
    </div>
  );
};

export default FreePuzzlePage;
